import { useTranslation } from 'react-i18next'
import InnerBanner from '../../../components/InnerBanner'
import DashboardWrapper from '../../Dashboard/components/DashboardWrapper'
import Badge from '../../../components/Badge/Badge'
import { useQuery } from '@tanstack/react-query'
import { TAGS } from '../../../services/Tags'
import { getCompanyInformation } from '../../../services/CompanyData'
import { useNavigate, useParams } from 'react-router-dom'
import CompanyDetailSkeleton from '../../../skeleton/Company/CompanyDetailSkeleton'
import Circle from '../../../components/Circle'
import ArrowLeftIcon from '../../../icons/ArrowLeftIcon'
import {
  IS_ADMIN,
  IS_HIRING_ADVISOR,
  ITEMS_PER_PAGE,
  VIEWPORT_LG,
} from '../../../utils/constants'
import useResize from '../../../hooks/useResize'
import ArrowRigthIcon from '../../../icons/ArrowRigthIcon'
import Toast from '../../../components/Toast/Toast'
import { useAdminCompaniesStore } from '../../../store'
import dayjs from 'dayjs'
import { PRIVATE_ROUTES } from '../../../Routes/routes'
import { listOfCompanies } from '../../../services/Admin'
import Button from '../../../components/Button'
import { useState } from 'react'
import ContractPreviewSection from '../../../components/ContractPreviewSection/ContractPreviewSection'
import {
  buildCompanyLoginUrl,
  extractCompanyTokenFromCompany,
  saveCompanyCredentialsById,
} from '../../../utils/companyLogin'

const CompanyDetail = ({ seeFromHA }) => {
  const [showPassword, setShowPassword] = useState(false)
  const { page, setPage, search, tabBarValue } = useAdminCompaniesStore()

  const navigate = useNavigate()
  const { t } = useTranslation()
  const { id } = useParams()
  const viewportSize = useResize()
  const isMobile = viewportSize[0] > 0 && viewportSize[0] <= VIEWPORT_LG
  const {
    data: companyInformationData = [],
    isInitialLoading: isLoadingCompanyDetailFromAdmin,
  } = useQuery([TAGS.company_detail, id], () => getCompanyInformation(id), {
    enabled: id !== undefined,
  })

  const {
    data: listOfCompaniesData = [],
    isInitialLoading: isLoadingListOfCompanies,
  } = useQuery([TAGS.list_of_companies, page, search, tabBarValue], () =>
    listOfCompanies({
      page,
      searchByText: search,
      status: tabBarValue,
      companyAccount: false,
    })
  )

  const {
    name = '',
    address = {},
    review = '',
    create_date = '',
    contact_name = '',
    email = '',
    phone = '',
    legal_contact_name = '',
    legal_contact_dni = '',
    legal_contact_phone = '',
    billing_name = '',
    user = '',
    vat = '',
    contract_url = '',
    contract = null,
  } = companyInformationData ?? []
  const today = dayjs().format('DD-MM-YYYY')
  const transformCreateDay = dayjs(create_date)?.format('DD-MM-YYYY')
  const isSameDate = transformCreateDay === today

  const companyTokenFromApi = extractCompanyTokenFromCompany(
    companyInformationData
  )

  // Migrar credenciales pendientes (guardadas por nombre) al ID real de la empresa
  if (id && name) {
    const pending = JSON.parse(
      localStorage.getItem('company_credentials_pending') || '{}'
    )
    const pendingKey = name.trim().toLowerCase()
    if (pending[pendingKey]) {
      const merged = {
        ...pending[pendingKey],
        companyToken: companyTokenFromApi ?? pending[pendingKey].companyToken,
      }
      saveCompanyCredentialsById(id, merged)
      delete pending[pendingKey]
      localStorage.setItem(
        'company_credentials_pending',
        JSON.stringify(pending)
      )
    }
  }

  const handleNextCompany = async () => {
    try {
      const totalPages = Math.ceil(listOfCompaniesData?.count / ITEMS_PER_PAGE)

      const currentIndex = listOfCompaniesData?.list?.findIndex(
        (company) => Number(company.id) === Number(id)
      )
      const nextIndex = currentIndex + 1
      const lastItem = listOfCompaniesData?.list?.length - 1
      const lastPage = totalPages - 1

      const isLastItem = Number(currentIndex) === Number(lastItem)
      const isLastPage = page === lastPage

      if (isLastItem && !isLastPage) {
        await setPage(page + 1)
        const newCompanyData = await listOfCompanies({
          page: page + 1,
          searchByText: search,
          status: tabBarValue,
          companyAccount: false,
        })

        const renderNewCompnayData = newCompanyData?.list

        await navigate(`/company-detail/${renderNewCompnayData?.[0]?.id}`)
        return
      }

      if (isLastPage && isLastItem) {
        Toast({
          title: 'No hay más empresas',
          icon: 'info',
        })
        return
      }

      navigate(`/company-detail/${listOfCompaniesData?.list?.[nextIndex]?.id}`)
    } catch (error) {
      console.error('Error navigating to next talent:', error)
    }
  }

  const handlePreviousCompany = async () => {
    try {
      const currentIndex = listOfCompaniesData?.list?.findIndex(
        (company) => Number(company.id) === Number(id)
      )
      const previousIndex = currentIndex - 1
      const initialItem = 0
      const initialPage = 0

      const isInitialItem = Number(currentIndex) === Number(initialItem)
      const isInitialPage = page === initialPage

      if (isInitialItem && !isInitialPage) {
        await setPage(page - 1)
        const newCompanyData = await listOfCompanies({
          page: page - 1,
          searchByText: search,
          status: tabBarValue,
          companyAccount: false,
        })

        const renderCompanyData = newCompanyData?.list ?? []

        await navigate(`/company-detail/${renderCompanyData?.[9]?.id}`)
        return
      }

      if (isInitialPage && isInitialItem) {
        Toast({
          title: 'No hay más empresas',
          icon: 'info',
        })
        return
      }

      navigate(
        `/company-detail/${listOfCompaniesData?.list?.[previousIndex]?.id}`
      )
    } catch (error) {
      console.error('Error navigating to next talent:', error)
    }
  }

  return (
    <DashboardWrapper dashboardWrapperClassName="w-100">
      {viewportSize[0] >= VIEWPORT_LG && !seeFromHA && (
        <>
          <div
            className="position-absolute start-0 cursor-pointer"
            style={{
              marginLeft: '-60px',
              marginTop: '250px',
            }}
            onClick={handlePreviousCompany}
          >
            <Circle className="bg-white border border-on-bg-Tertiary p-4">
              <ArrowLeftIcon width="32" height="32" />
            </Circle>
          </div>
          <div
            className="position-absolute start-100 cursor-pointer ms-2"
            style={{
              marginTop: '250px',
            }}
            onClick={handleNextCompany}
          >
            <Circle className="bg-white border border-on-bg-Tertiary p-4">
              <ArrowRigthIcon width="32" height="32" />
            </Circle>
          </div>
        </>
      )}
      <div className="mt-5">
        <InnerBanner
          beforeRoute={IS_ADMIN ? PRIVATE_ROUTES.admin_companies : -1}
          before={IS_HIRING_ADVISOR ? t('my_enterprise') : t('companies')}
          text={t('company_details')}
        />

        {isLoadingCompanyDetailFromAdmin || isLoadingListOfCompanies ? (
          <CompanyDetailSkeleton />
        ) : (
          <div
            className={`${
              isMobile ? 'p-3' : 'p-5'
            } rounded border border-bg-blue bg-bg-blue-opacity mb-4 mt-2`}
          >
            <div
              className={`d-flex ${
                isMobile
                  ? 'flex-column gap-2 align-items-start'
                  : 'justify-content-between align-items-center'
              }`}
            >
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span className="fs-3 text-bg-blue fw-bolder text-break">
                  {name ?? t('not_available')}
                </span>
                {isSameDate && (
                  <Badge className="border border-on-bg-Success bg-bg-Success text-on-bg-Success">
                    {t('new')}
                  </Badge>
                )}
              </div>
              {IS_HIRING_ADVISOR && seeFromHA && (
                <div className={isMobile ? 'w-100' : ''}>
                  <Button
                    className={isMobile ? 'w-100' : ''}
                    onClick={() => {
                      navigate(`/list-of-offers/${id}`)
                    }}
                  >
                    Ver ofertas
                  </Button>
                </div>
              )}
            </div>
            <>
              {/* Sección de Datos Generales */}
              <div
                className={`rounded bg-white mt-4 ${isMobile ? 'p-3' : 'p-4'}`}
              >
                <h3 className="text-on-bg-primary fw-bolder mb-3">
                  Datos Generales
                </h3>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="text-on-bg-secondary font-small-2 mb-1">
                      Nombre del contacto
                    </label>
                    <p className="text-on-bg-primary mb-0">
                      {contact_name || t('not_available')}
                    </p>
                  </div>
                  <div className="col-md-6">
                    <label className="text-on-bg-secondary font-small-2 mb-1">
                      Correo de contacto
                    </label>
                    <p className="text-on-bg-primary mb-0">
                      {email || t('not_available')}
                    </p>
                  </div>
                  <div className="col-md-6">
                    <label className="text-on-bg-secondary font-small-2 mb-1">
                      Número de teléfono
                    </label>
                    <p className="text-on-bg-primary mb-0">
                      {phone || t('not_available')}
                    </p>
                  </div>
                  <div className="col-md-6">
                    <label className="text-on-bg-secondary font-small-2 mb-1">
                      Nombre del representante legal
                    </label>
                    <p className="text-on-bg-primary mb-0">
                      {legal_contact_name || t('not_available')}
                    </p>
                  </div>
                  <div className="col-md-6">
                    <label className="text-on-bg-secondary font-small-2 mb-1">
                      DNI del representante legal
                    </label>
                    <p className="text-on-bg-primary mb-0">
                      {legal_contact_dni || t('not_available')}
                    </p>
                  </div>
                  <div className="col-md-6">
                    <label className="text-on-bg-secondary font-small-2 mb-1">
                      Teléfono del representante legal
                    </label>
                    <p className="text-on-bg-primary mb-0">
                      {legal_contact_phone || t('not_available')}
                    </p>
                  </div>
                  <div className="col-md-6">
                    <label className="text-on-bg-secondary font-small-2 mb-1">
                      Descripción de la empresa
                    </label>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: review ?? '',
                      }}
                      className="text-on-bg-primary"
                    />
                  </div>
                </div>
              </div>

              {/* Sección de Información de Facturación */}
              <div className={`rounded bg-white mt-3 ${isMobile ? 'p-3' : 'p-4'}`}>
                <h3 className="text-on-bg-primary fw-bolder mb-3">
                  Información de Facturación
                </h3>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="text-on-bg-secondary font-small-2 mb-1">
                      Contacto de facturación
                    </label>
                    <p className="text-on-bg-primary mb-0">
                      {billing_name || t('not_available')}
                    </p>
                  </div>
                  <div className="col-md-6">
                    <label className="text-on-bg-secondary font-small-2 mb-1">
                      Correo de facturación
                    </label>
                    <p className="text-on-bg-primary mb-0">
                      {user?.email || t('not_available')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Sección de Ubicación */}
              <div className={`rounded bg-white mt-3 ${isMobile ? 'p-3' : 'p-4'}`}>
                <h3 className="text-on-bg-primary fw-bolder mb-3">Ubicación</h3>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="text-on-bg-secondary font-small-2 mb-1">
                      País
                    </label>
                    <p className="text-on-bg-primary mb-0">
                      {address?.country?.country_name ?? t('not_available')}
                    </p>
                  </div>
                  <div className="col-md-4">
                    <label className="text-on-bg-secondary font-small-2 mb-1">
                      Estado
                    </label>
                    <p className="text-on-bg-primary mb-0">
                      {address?.state?.state_name ?? t('not_available')}
                    </p>
                  </div>
                  <div className="col-md-4">
                    <label className="text-on-bg-secondary font-small-2 mb-1">
                      Ciudad
                    </label>
                    <p className="text-on-bg-primary mb-0">
                      {address?.city?.city_name ?? t('not_available')}
                    </p>
                  </div>
                  <div className="col-md-4">
                    <label className="text-on-bg-secondary font-small-2 mb-1">
                      CIF
                    </label>
                    <p className="text-on-bg-primary mb-0">
                      {vat || t('not_available')}
                    </p>
                  </div>
                  <div className="col-md-4">
                    <label className="text-on-bg-secondary font-small-2 mb-1">
                      Código postal
                    </label>
                    <p className="text-on-bg-primary mb-0">
                      {address?.zip_code ?? t('not_available')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Sección de Información Adicional */}
              {contract_url && (
                <div
                  className={`rounded bg-white mt-3 ${isMobile ? 'p-3' : 'p-4'}`}
                >
                  <h3 className="text-on-bg-primary fw-bolder mb-3">
                    Información Adicional
                  </h3>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="text-on-bg-secondary font-small-2 mb-1">
                        Contrato
                      </label>
                      <a
                        href={contract_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-bg-CTA-primary text-decoration-underline d-block"
                      >
                        Ver contrato
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {/* Sección de Acceso Inicial */}
              {(() => {
                const all = JSON.parse(
                  localStorage.getItem('company_credentials') || '{}'
                )
                const creds = all[id]
                if (!creds && !companyTokenFromApi) return null

                const companyToken = companyTokenFromApi ?? creds?.companyToken
                const loginUrl =
                  buildCompanyLoginUrl(companyToken) || creds?.loginUrl
                if (!creds?.username && !loginUrl) return null

                return (
                  <div
                    className={`rounded bg-white mt-3 ${isMobile ? 'p-3' : 'p-4'}`}
                  >
                    <h3 className="text-on-bg-primary fw-bolder mb-3">
                      Acceso Inicial
                    </h3>
                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="text-on-bg-secondary font-small-2 mb-1">
                          Usuario
                        </label>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <p className="text-on-bg-primary mb-0 fw-bold text-break">
                            {creds?.username || t('not_available')}
                          </p>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary py-0 px-2"
                            disabled={!creds?.username}
                            onClick={() => {
                              if (!creds?.username) return
                              navigator.clipboard.writeText(creds.username)
                              Toast({ title: 'Usuario copiado', timer: 1500 })
                            }}
                          >
                            Copiar
                          </button>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <label className="text-on-bg-secondary font-small-2 mb-1">
                          Contraseña
                        </label>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <p
                            className="text-on-bg-primary mb-0 fw-bold"
                            style={{
                              letterSpacing: showPassword ? 'normal' : '2px',
                            }}
                          >
                            {creds?.password
                              ? showPassword
                                ? creds.password
                                : '••••••••••••'
                              : t('not_available')}
                          </p>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary py-0 px-2"
                            disabled={!creds?.password}
                            onClick={() => setShowPassword((v) => !v)}
                          >
                            {showPassword ? 'Ocultar' : 'Ver'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary py-0 px-2"
                            disabled={!creds?.password}
                            onClick={() => {
                              navigator.clipboard.writeText(creds.password)
                              Toast({
                                title: 'Contraseña copiada',
                                timer: 1500,
                              })
                            }}
                          >
                            Copiar
                          </button>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <label className="text-on-bg-secondary font-small-2 mb-1">
                          Link de acceso
                        </label>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <a
                            href={loginUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-bg-CTA-primary text-decoration-underline"
                          >
                            Abrir sesión
                          </a>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary py-0 px-2"
                            disabled={!loginUrl}
                            onClick={() => {
                              navigator.clipboard.writeText(loginUrl)
                              Toast({ title: 'Link copiado', timer: 1500 })
                            }}
                          >
                            Copiar
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })()}

              {contract && (
                <div className='mt-4'>
                  <ContractPreviewSection contract={contract} />
                </div>
              )}
            </>
          </div>
        )}
      </div>
    </DashboardWrapper>
  )
}

export default CompanyDetail
